/**
 * TOSS Web Application — панель калькулятора
 *
 * Карточка на канве, а не стенка у края окна. До 27.08.2026 панель была последней поверхностью
 * админки, оставшейся от прежнего языка: белая колонка 320 px, прижатая к правому краю с
 * `border-l`, без радиуса и без поля вокруг — тогда как боковое меню, шапка и содержимое к тому
 * времени уже стали белыми карточками на сером поле (`SidebarShell`, `TopBar variant="card"`).
 *
 * Вместе с формой ушли три вещи внутри:
 *
 *  - **вложенная плашка дисплея.** Серый прямоугольник на белой панели — это карточка в карточке;
 *    выражение и результат печатаются прямо на поверхности, а от клавиатуры их отделяет линия;
 *  - **синяя плита из пяти кнопок.** `--color-accent-500` численно равен `--color-primary-500`,
 *    поэтому «=» ничем не отличался от «÷», и правый столбец читался одним объектом. Залит теперь
 *    только «=»; операторы — светлая ступень того же тона, иерархию держит заливка, а не цвет;
 *  - **строка «Можно использовать клавиатуру».** Постоянная серая подпись под контролами заменена
 *    иконкой ⓘ у заголовка: тот же текст, но по наведению.
 *
 * **`containerRef` и `tabIndex` живут на внешнем `<aside>`, и это не случайность.** `useCalculator`
 * слушает `keydown` глобально и отвечает только пока `containerRef.current` содержит
 * `document.activeElement`. Уедет ref на внутреннюю карточку, а фокус останется на обёртке — ввод
 * с клавиатуры молча перестанет работать. Держит тест «набор с клавиатуры» в `CalculatorPanel.test.tsx`.
 *
 * Отступы вокруг карточки задаёт оболочка (`AppLayout` через `className`), как и у шапки: только
 * она знает, по какой вертикали выравнивать панель с содержимым — на обычном экране и на
 * мессенджере они разные.
 */

import { useEffect, useState } from 'react';
import { X, Trash2, Check, Copy, Info } from 'lucide-react';
import { useCalculator, type CalculatorHistoryItem } from '@/hooks';
import { Tooltip } from '@/components/sidebar';
import { useCalculatorPanel } from '@/store/uiStore';

// ============================================================================
// Types
// ============================================================================

export interface CalculatorPanelProps {
  /** Отступы вокруг карточки — их задаёт оболочка, см. докблок. */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Текст ⓘ. Коротко намеренно: тултип не переносит строк (`whitespace-nowrap`), и длинная подсказка
 * уехала бы полосой через весь экран. Полный набор клавиш описан в `useCalculator`.
 */
const KEYBOARD_HINT = 'Цифры и + − × ÷ — с клавиатуры, Enter — равно, Esc — сброс';

/** Сколько строк истории показывает панель. Остальное живёт в localStorage до следующего сеанса. */
const HISTORY_ROWS = 10;

// ============================================================================
// Calculator Button Component
// ============================================================================

type ButtonVariant = 'number' | 'operator' | 'equals' | 'function';

interface CalcButtonProps {
  label: string;
  onClick: () => void;
  variant?: ButtonVariant;
}

function CalcButton({ label, onClick, variant = 'number' }: CalcButtonProps): JSX.Element {
  const variantClasses: Record<ButtonVariant, string> = {
    number: `
      bg-[var(--color-bg-secondary)]
      text-[var(--color-text-primary)]
      hover:bg-[var(--color-bg-tertiary)]
      focus-visible:ring-[var(--color-border-focus)]
    `,
    function: `
      bg-[var(--color-bg-tertiary)]
      text-[var(--color-text-secondary)]
      hover:bg-[var(--color-border)]
      focus-visible:ring-[var(--color-border-focus)]
    `,
    operator: `
      bg-[var(--color-primary-50)]
      text-[var(--color-primary-600)]
      hover:bg-[var(--color-primary-100)]
      focus-visible:ring-[var(--color-primary-500)]
    `,
    equals: `
      bg-[var(--color-primary-500)]
      text-[var(--color-text-on-primary)]
      hover:bg-[var(--color-primary-600)]
      focus-visible:ring-[var(--color-primary-500)]
    `,
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        flex items-center justify-center
        h-[var(--h-control)]
        rounded-[var(--radius-md)]
        text-base font-medium
        select-none
        transition-colors duration-150
        active:scale-[0.97]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1
        ${variantClasses[variant]}
      `}
    >
      {label}
    </button>
  );
}

// ============================================================================
// Calculator Display Component
// ============================================================================

interface DisplayProps {
  value: string;
  expression: string;
}

function Display({ value, expression }: DisplayProps): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="
        flex-shrink-0
        px-4 py-3
        text-right
        border-b border-[var(--color-border-light)]
      "
    >
      <div className="text-sm text-[var(--color-text-secondary)] min-h-[20px] truncate">
        {expression || '\u00A0'}
      </div>
      <div className="mt-0.5 text-3xl font-semibold text-[var(--color-text-primary)] truncate">
        {value}
      </div>
    </div>
  );
}

// ============================================================================
// History Row Component
// ============================================================================

interface HistoryRowProps {
  item: CalculatorHistoryItem;
  onUse: () => void;
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  const isToday = date.toDateString() === new Date().toDateString();

  if (isToday) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }

  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Строка истории: слева выражение, справа результат.
 *
 * Две кнопки, а не кнопка внутри кнопки, как было раньше: вложенный `<button>` — невалидная
 * разметка, и копирование ловило клик «подставить» только благодаря `stopPropagation`.
 * Время расчёта ушло из строки в её `title` — в списке из десяти строк оно было третьей колонкой,
 * на которую никто не смотрит.
 */
function HistoryRow({ item, onUse }: HistoryRowProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(item.result.replace(/\s/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <li className="group flex items-center transition-colors hover:bg-[var(--color-bg-secondary)]">
      <button
        type="button"
        onClick={onUse}
        title={`${formatTime(item.timestamp)} — подставить результат`}
        className="
          flex-1 min-w-0
          flex items-baseline justify-between gap-3
          py-2 pl-4 pr-1 text-left
          focus:outline-none focus-visible:bg-[var(--color-bg-secondary)]
        "
      >
        <span className="text-xs text-[var(--color-text-secondary)] truncate">
          {item.expression}
        </span>
        <span className="text-sm text-[var(--color-text-primary)] flex-shrink-0">
          {item.result}
        </span>
      </button>

      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? 'Скопировано' : 'Скопировать результат'}
        className={`
          flex-shrink-0 mr-2 p-1 rounded-[var(--radius-sm)]
          transition-all
          focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]
          ${copied
            ? 'text-[var(--color-success)] opacity-100'
            : 'text-[var(--color-text-disabled)] opacity-0 group-hover:opacity-100 hover:text-[var(--color-primary-500)]'
          }
        `}
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </li>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function CalculatorPanel({ className = '' }: CalculatorPanelProps): JSX.Element | null {
  const { isOpen, close } = useCalculatorPanel();
  const {
    display,
    expression,
    history,
    inputDigit,
    inputDecimal,
    inputOperation,
    calculate,
    clearAll,
    backspace,
    toggleSign,
    percentage,
    clearHistory,
    // Локальное имя: `useHistoryItem` в колбэке eslint читает как вызов ХУКА внутри callback.
    useHistoryItem: applyHistoryItem,
    containerRef,
  } = useCalculator();

  // Handle Escape key to close panel
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, close]);

  // Focus container when panel opens
  useEffect(() => {
    if (isOpen && containerRef.current) {
      containerRef.current.focus();
    }
  }, [isOpen, containerRef]);

  // Don't render if closed
  if (!isOpen) {
    return null;
  }

  return (
    <aside
      ref={containerRef}
      tabIndex={-1}
      className={`
        hidden lg:flex flex-shrink-0
        page-padding-r
        focus:outline-none
        ${className}
      `}
      role="complementary"
      aria-label="Калькулятор"
    >
      <div
        className="app-surface w-[320px] h-full flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div
          className="
            flex-shrink-0
            flex items-center justify-between gap-2
            px-4 py-3
            border-b border-[var(--color-border)]
          "
        >
          <div className="flex items-center gap-1 min-w-0">
            <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
              Калькулятор
            </h2>

            <Tooltip content={KEYBOARD_HINT} side="left">
              <button
                type="button"
                aria-label="Подсказка по клавиатуре"
                className="
                  flex items-center justify-center
                  -m-1 p-1 rounded-[var(--radius-sm)]
                  text-[var(--color-text-disabled)]
                  hover:text-[var(--color-text-secondary)]
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]
                  transition-colors
                "
              >
                {/* 14 px и обводка тоньше стандартных: при 16/2 кружок спорит с заголовком в 15 px
                    и читается нашлёпкой, а не пометкой. Область нажатия добирается `-m-1 p-1` —
                    24 px под курсор, не сдвигая иконку от слова. */}
                <Info className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            </Tooltip>
          </div>

          <button
            type="button"
            onClick={close}
            className="
              flex-shrink-0
              p-1.5 -mr-1
              text-[var(--color-text-secondary)]
              hover:text-[var(--color-text-primary)]
              hover:bg-[var(--color-bg-secondary)]
              rounded-[var(--radius-md)]
              transition-colors
            "
            aria-label="Закрыть калькулятор"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Display */}
        <Display value={display} expression={expression} />

        {/* Button Grid */}
        <div className="flex-shrink-0 grid grid-cols-4 gap-2 p-4">
          {/* Row 1: C, ←, %, ÷ */}
          <CalcButton label="C" onClick={clearAll} variant="function" />
          <CalcButton label="←" onClick={backspace} variant="function" />
          <CalcButton label="%" onClick={percentage} variant="function" />
          <CalcButton label="÷" onClick={() => inputOperation('÷')} variant="operator" />

          {/* Row 2: 7, 8, 9, × */}
          <CalcButton label="7" onClick={() => inputDigit('7')} />
          <CalcButton label="8" onClick={() => inputDigit('8')} />
          <CalcButton label="9" onClick={() => inputDigit('9')} />
          <CalcButton label="×" onClick={() => inputOperation('×')} variant="operator" />

          {/* Row 3: 4, 5, 6, − */}
          <CalcButton label="4" onClick={() => inputDigit('4')} />
          <CalcButton label="5" onClick={() => inputDigit('5')} />
          <CalcButton label="6" onClick={() => inputDigit('6')} />
          <CalcButton label="−" onClick={() => inputOperation('-')} variant="operator" />

          {/* Row 4: 1, 2, 3, + */}
          <CalcButton label="1" onClick={() => inputDigit('1')} />
          <CalcButton label="2" onClick={() => inputDigit('2')} />
          <CalcButton label="3" onClick={() => inputDigit('3')} />
          <CalcButton label="+" onClick={() => inputOperation('+')} variant="operator" />

          {/* Row 5: 0, +/−, ., = */}
          <CalcButton label="0" onClick={() => inputDigit('0')} />
          <CalcButton label="+/−" onClick={toggleSign} variant="function" />
          <CalcButton label="," onClick={inputDecimal} />
          <CalcButton label="=" onClick={calculate} variant="equals" />
        </div>

        {/* History — единственная прокрутка панели: клавиатура остаётся на месте */}
        {history.length > 0 && (
          <div className="flex-1 min-h-0 flex flex-col border-t border-[var(--color-border)]">
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-2">
              <h3 className="text-sm font-medium text-[var(--color-text-primary)]">История</h3>
              <button
                type="button"
                onClick={clearHistory}
                className="
                  flex items-center gap-1
                  px-1 py-0.5 rounded-[var(--radius-sm)]
                  text-xs text-[var(--color-text-secondary)]
                  hover:text-[var(--color-error)]
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]
                  transition-colors
                "
              >
                <Trash2 className="w-3 h-3" />
                Очистить
              </button>
            </div>

            <ul className="flex-1 min-h-0 overflow-y-auto divide-y divide-[var(--color-border-light)]">
              {history.slice(0, HISTORY_ROWS).map((item) => (
                <HistoryRow key={item.id} item={item} onUse={() => applyHistoryItem(item)} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </aside>
  );
}

export default CalculatorPanel;
